"use client";

import React, { useId } from "react";
import { LoyaltyProgramEnum } from "@/generated/email_parser.openapi";
import {
  loyaltyHexColorMapping,
  stringToLoyaltyProgramEnum,
} from "@/lib/loyaltyUtils";

/**
 * Creates a tint (mixes color with white)
 * @param hex - Hex color string
 * @param percent - Percentage of white to mix (0-100)
 * @returns Tinted hex color
 */
function tintHex(hex: string, percent: number): string {
  const num = parseInt(hex.replace("#", ""), 16);
  const r = (num >> 16) & 0xff;
  const g = (num >> 8) & 0xff;
  const b = num & 0xff;

  // Mix with white
  const newR = Math.round(r + (255 - r) * (percent / 100));
  const newG = Math.round(g + (255 - g) * (percent / 100));
  const newB = Math.round(b + (255 - b) * (percent / 100));

  return `#${newR.toString(16).padStart(2, "0")}${newG.toString(16).padStart(2, "0")}${newB.toString(16).padStart(2, "0")}`;
}

/**
 * Creates a shade (mixes color with black)
 * @param hex - Hex color string
 * @param percent - Percentage of black to mix (0-100)
 * @returns Shaded hex color
 */
function shadeHex(hex: string, percent: number): string {
  const num = parseInt(hex.replace("#", ""), 16);
  const r = (num >> 16) & 0xff;
  const g = (num >> 8) & 0xff;
  const b = num & 0xff;

  // Mix with black
  const newR = Math.round(r * (1 - percent / 100));
  const newG = Math.round(g * (1 - percent / 100));
  const newB = Math.round(b * (1 - percent / 100));

  return `#${newR.toString(16).padStart(2, "0")}${newG.toString(16).padStart(2, "0")}${newB.toString(16).padStart(2, "0")}`;
}

interface LoyaltyPointsIconProps {
  program?: LoyaltyProgramEnum | string;
  size?: number;
  className?: string;
}

/**
 * Dynamic loyalty points icon that changes color based on the loyalty program
 */
export function LoyaltyPointsIcon({
  program,
  size = 32,
  className,
}: LoyaltyPointsIconProps) {
  // Generate unique ID for this component instance to avoid gradient conflicts
  const uniqueId = useId();
  const baseId = `loyalty-points-${uniqueId.replace(/:/g, "-")}`;

  // Convert to enum
  const loyaltyProgram: LoyaltyProgramEnum | undefined = (() => {
    // If program is provided as enum, use it directly
    if (program && typeof program !== "string") {
      return program;
    }
    // If program is provided as string, try to convert it
    if (program && typeof program === "string") {
      return stringToLoyaltyProgramEnum(program);
    }
    return undefined;
  })();
  const mainColor =
    loyaltyProgram && loyaltyHexColorMapping[loyaltyProgram]
      ? loyaltyHexColorMapping[loyaltyProgram]!
      : "#CC2B48"; // Default fallback color

  // Create gradient colors based on the main brand color
  // For fill gradient: darker to lighter (similar to original #CC2B48 to #FF6883)
  const fillStartColor = shadeHex(mainColor, 15); // Darker variant
  const fillEndColor = tintHex(mainColor, 25); // Lighter variant

  // For border gradient: match the original pattern from the SVG
  // Original pattern: #FF0000 → #FCCACA (22.6%) → #D81616 (43.75%) → #F7C5C5 (67.8%) → #F15050 (83.7%)
  // Analysis:
  // - #FCCACA is ~79% white tint of #FF0000 (very light pink)
  // - #D81616 is ~9% black shade of #FF0000 (darker red)
  // - #F7C5C5 is ~77% white tint of #FF0000 (light pink)
  // - #F15050 is ~31% white tint of #FF0000 (medium red-pink)
  const borderColor1 = mainColor; // Main color (like #FF0000)
  const borderColor2 = tintHex(mainColor, 79); // Very light tint (like #FCCACA)
  const borderColor3 = shadeHex(mainColor, 9); // Darker shade (like #D81616)
  const borderColor4 = tintHex(mainColor, 77); // Light tint (like #F7C5C5)
  const borderColor5 = tintHex(mainColor, 31); // Medium tint (like #F15050)

  // Circle gradients
  // paint2: Circle fill - #830119 to #C00124 (very dark shades)
  // paint3: Circle stroke - #FF7790 to #C42B47 (light tint to medium shade)
  const circleFillStart = shadeHex(mainColor, 48); // Very dark shade (like #830119)
  const circleFillEnd = shadeHex(mainColor, 25); // Dark shade (like #C00124)
  const circleStrokeStart = tintHex(mainColor, 52); // Light tint (like #FF7790)
  const circleStrokeEnd = shadeHex(mainColor, 23); // Medium shade (like #C42B47)

  // Path gradients
  // paint4: Path fill - #830119 to #BC0123 (very dark shades)
  // paint5: Path stroke - #FF7790 to #C42B47 (light tint to medium shade)
  const pathFillStart = shadeHex(mainColor, 48); // Very dark shade (like #830119)
  const pathFillEnd = shadeHex(mainColor, 26); // Dark shade (like #BC0123)
  const pathStrokeStart = tintHex(mainColor, 52); // Light tint (like #FF7790)
  const pathStrokeEnd = shadeHex(mainColor, 23); // Medium shade (like #C42B47)

  // Generate unique IDs for gradients to avoid conflicts
  const gradientId1 = `${baseId}-gradient-1`;
  const gradientId2 = `${baseId}-gradient-2`;
  const gradientId3 = `${baseId}-gradient-3`;
  const gradientId4 = `${baseId}-gradient-4`;
  const gradientId5 = `${baseId}-gradient-5`;
  const gradientId6 = `${baseId}-gradient-6`;
  const maskId = `${baseId}-mask`;

  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 32 32"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className={className || "object-contain"}
      style={{ display: "block", flexShrink: 0 }}
    >
      <defs>
        {/* Fill gradient - from darker to lighter */}
        <linearGradient
          id={gradientId1}
          x1="23.1579"
          y1="0.421053"
          x2="13.4737"
          y2="32.8421"
          gradientUnits="userSpaceOnUse"
        >
          <stop stopColor={fillStartColor} />
          <stop offset="1" stopColor={fillEndColor} />
        </linearGradient>
        {/* Border gradient - complex multi-stop gradient */}
        <linearGradient
          id={gradientId2}
          x1="5.92909e-07"
          y1="32"
          x2="32"
          y2="5.47368"
          gradientUnits="userSpaceOnUse"
        >
          <stop stopColor={borderColor1} />
          <stop offset="0.225962" stopColor={borderColor2} />
          <stop offset="0.4375" stopColor={borderColor3} />
          <stop offset="0.677885" stopColor={borderColor4} />
          <stop offset="0.836538" stopColor={borderColor5} />
        </linearGradient>
        {/* Circle fill gradient */}
        <linearGradient
          id={gradientId3}
          x1="13.4741"
          y1="11.7895"
          x2="13.4741"
          y2="25.2632"
          gradientUnits="userSpaceOnUse"
        >
          <stop stopColor={circleFillStart} />
          <stop offset="1" stopColor={circleFillEnd} />
        </linearGradient>
        {/* Circle stroke gradient */}
        <linearGradient
          id={gradientId4}
          x1="13.4741"
          y1="11.7895"
          x2="13.4741"
          y2="25.2632"
          gradientUnits="userSpaceOnUse"
        >
          <stop stopColor={circleStrokeStart} />
          <stop offset="1" stopColor={circleStrokeEnd} />
        </linearGradient>
        {/* Path fill gradient */}
        <linearGradient
          id={gradientId5}
          x1="18.8247"
          y1="6.73682"
          x2="18.8247"
          y2="19.6138"
          gradientUnits="userSpaceOnUse"
        >
          <stop stopColor={pathFillStart} />
          <stop offset="1" stopColor={pathFillEnd} />
        </linearGradient>
        {/* Path stroke gradient */}
        <linearGradient
          id={gradientId6}
          x1="18.8247"
          y1="6.73682"
          x2="24.0009"
          y2="18.105"
          gradientUnits="userSpaceOnUse"
        >
          <stop stopColor={pathStrokeStart} />
          <stop offset="1" stopColor={pathStrokeEnd} />
        </linearGradient>
      </defs>
      <rect
        x="0.421053"
        y="0.421053"
        width="31.1579"
        height="31.1579"
        rx="8"
        fill={`url(#${gradientId1})`}
      />
      <rect
        x="0.421053"
        y="0.421053"
        width="31.1579"
        height="31.1579"
        rx="8"
        stroke={`url(#${gradientId2})`}
        strokeWidth="0.842105"
      />
      {/* Circle */}
      <circle
        cx="13.4741"
        cy="18.5263"
        r="6.52632"
        fill={`url(#${gradientId3})`}
        stroke={`url(#${gradientId4})`}
        strokeWidth="0.421053"
      />
      {/* Path with mask */}
      <mask id={maskId} fill="white">
        <path d="M18.5264 6.73682C22.247 6.73684 25.2627 9.75348 25.2627 13.4741C25.2625 16.2063 23.6355 18.5569 21.2979 19.6138C21.6801 18.7683 21.8945 17.8305 21.8945 16.8423C21.8945 13.1217 18.8787 10.1051 15.1582 10.105C14.1701 10.105 13.2321 10.3195 12.3867 10.7017C13.4438 8.36416 15.7942 6.73682 18.5264 6.73682Z" />
      </mask>
      <path
        d="M18.5264 6.73682C22.247 6.73684 25.2627 9.75348 25.2627 13.4741C25.2625 16.2063 23.6355 18.5569 21.2979 19.6138C21.6801 18.7683 21.8945 17.8305 21.8945 16.8423C21.8945 13.1217 18.8787 10.1051 15.1582 10.105C14.1701 10.105 13.2321 10.3195 12.3867 10.7017C13.4438 8.36416 15.7942 6.73682 18.5264 6.73682Z"
        fill={`url(#${gradientId5})`}
      />
      <path
        d="M18.5264 6.73682L18.5264 6.31576H18.5264V6.73682ZM25.2627 13.4741L25.6837 13.4741V13.4741H25.2627ZM21.2979 19.6138L20.9142 19.4403L20.4545 20.4571L21.4713 19.9974L21.2979 19.6138ZM21.8945 16.8423L22.3156 16.8423V16.8423H21.8945ZM15.1582 10.105L15.1582 9.68393H15.1582V10.105ZM12.3867 10.7017L12.0031 10.5282L11.5433 11.545L12.5601 11.0853L12.3867 10.7017ZM18.5264 6.73682L18.5264 7.15787C22.0144 7.1579 24.8416 9.98595 24.8416 13.4741H25.2627H25.6837C25.6837 9.52102 22.4796 6.31579 18.5264 6.31576L18.5264 6.73682ZM25.2627 13.4741L24.8416 13.4741C24.8415 16.0348 23.3171 18.2388 21.1244 19.2301L21.2979 19.6138L21.4713 19.9974C23.954 18.875 25.6836 16.3779 25.6837 13.4741L25.2627 13.4741ZM21.2979 19.6138L21.6815 19.7872C22.0877 18.8888 22.3156 17.892 22.3156 16.8423L21.8945 16.8423L21.4735 16.8423C21.4735 17.769 21.2725 18.6477 20.9142 19.4403L21.2979 19.6138ZM21.8945 16.8423H22.3156C22.3156 12.8893 19.1113 9.6841 15.1582 9.68393L15.1582 10.105L15.1582 10.526C18.6461 10.5262 21.4735 13.3542 21.4735 16.8423H21.8945ZM15.1582 10.105V9.68393C14.1086 9.68393 13.1116 9.91194 12.2133 10.318L12.3867 10.7017L12.5601 11.0853C13.3527 10.7271 14.2317 10.526 15.1582 10.526V10.105ZM12.3867 10.7017L12.7704 10.8752C13.7619 8.6826 15.9658 7.15787 18.5264 7.15787V6.73682V6.31576C15.6225 6.31576 13.1257 8.04572 12.0031 10.5282L12.3867 10.7017Z"
        fill={`url(#${gradientId6})`}
        mask={`url(#${maskId})`}
      />
    </svg>
  );
}
